if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (!this.mql.matches && this.elements.viewer.slider) {
          this.elements.viewer.slider.addEventListener(
            'scroll',
            debounce(this.onMobileSliderScroll.bind(this), 150)
          );
          this.onMobileSliderScroll();
        }

        this.viewerPrevBtn = this.querySelector('button[name="gallery-previous"]');
        this.viewerNextBtn = this.querySelector('button[name="gallery-next"]');
        if (this.viewerPrevBtn && this.viewerNextBtn) {
          this.viewerPrevBtn.addEventListener('click', () => this.navigateViewer(-1));
          this.viewerNextBtn.addEventListener('click', () => this.navigateViewer(1));
          this.updateViewerNavState();
        }
      }

      navigateViewer(direction) {
        if (!this.elements.thumbnails) return;
        const thumbItems = Array.from(
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item:not(.thumbnail-list__item--cover)')
        );
        const currentIdx = thumbItems.findIndex((li) => li.querySelector('button[aria-current]'));
        const startIdx = currentIdx === -1 ? (direction > 0 ? -1 : thumbItems.length) : currentIdx;
        const targetIdx = startIdx + direction;
        if (targetIdx < 0 || targetIdx >= thumbItems.length) return;
        thumbItems[targetIdx].querySelector('button').click();
      }

      updateViewerNavState() {
        if (!this.viewerPrevBtn || !this.viewerNextBtn || !this.elements.thumbnails) return;
        const thumbItems = Array.from(
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item:not(.thumbnail-list__item--cover)')
        );
        const currentIdx = thumbItems.findIndex((li) => li.querySelector('button[aria-current]'));
        this.viewerPrevBtn.toggleAttribute('disabled', currentIdx <= 0);
        this.viewerNextBtn.toggleAttribute('disabled', currentIdx >= thumbItems.length - 1 || currentIdx === -1);
      }

      onMobileSliderScroll() {
        const slider = this.elements.viewer.slider;
        const slides = Array.from(this.elements.viewer.sliderItems).filter((element) => element.clientWidth > 0);
        if (!slides.length) return;
        const current = slides.reduce((closest, slide) =>
          Math.abs(slide.offsetLeft - slider.scrollLeft) < Math.abs(closest.offsetLeft - slider.scrollLeft)
            ? slide
            : closest
        );
        if (current.classList.contains('is-active')) return;
        window.pauseAllMedia();
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        current.classList.add('is-active');
        this.setActiveThumbnail(this.elements.thumbnails.querySelector(`[data-target="${current.dataset.mediaId}"]`));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        activeMedia?.classList?.remove('scroll-trigger--offscreen');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.updateViewerNavState();
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('media-gallery').forEach((gallery) => {
    const activeItem = gallery.querySelector('.product__media-item.is-active');
    // Use class check (not computed style) — CSS may not be applied yet at DOMContentLoaded
    if (!activeItem || !activeItem.classList.contains('product__media-item--cover')) return;
    const firstThumb = gallery.querySelector(
      '.thumbnail-list__item:not(.thumbnail-list__item--cover) button'
    );
    if (firstThumb) firstThumb.click();
  });
});
